import {useEffect, useRef, useState} from 'react';
import {useLocation, useNavigate, useParams} from 'react-router';

import {profileDetailsTabListTabT} from '@/lib/types/form.types';

import {createDataTableConfig} from '@/lib/helpers/config';

import {useItemQuery} from '@/hooks';

import {useNotifications} from '@/providers/components/notificationsProvider';

import {ProfileCardComponent, ProfileDetailsTabListComponent, profileCardSectionT} from '@/components';
import Page from '@/components/Page';
import {DataTable} from '@/components/dataTable';

import {useOrder} from '../../providers/orderDetailsProvider';

/**
 * TransferManifestDetails Component
 *
 * This component displays detailed information about a transfer manifest, including
 * packages and deliveries associated with the transfer.
 *
 * @component
 */
function TransferManifestDetails() {
  // hooks
  const {toast} = useNotifications();
  const navigate = useNavigate();
  const {transfer_id, id: orderId} = useParams();
  const location = useLocation();
  const {orderItems} = useOrder();

  // state
  const [packageCount, setPackageCount] = useState<number | null>(null);
  const [itemCount, setItemCount] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // refs
  const countsInitialized = useRef(false);

  /**
   * Effect to handle navigation and set counts
   */
  useEffect(() => {
    if (!transfer_id) {
      navigate(`/admin/orders/${orderId}`);
    }
    if (!countsInitialized.current) {
      // Retrieve counts from location state or URL parameters
      setPackageCount(location.state?.packageCount);
      setItemCount(location.state?.itemCount);
      countsInitialized.current = true;
    }
  }, [transfer_id, navigate, location.state, orderId]);

  /**
   * Query to fetch transfer manifest data
   */
  const {data: transferManifest, error: transferManifestError} = useItemQuery({
    table: 'Metric_Transfer',
    idColumn: 'metrc_transfer_id',
    columns: ['*'],
    id: parseInt(transfer_id || '0'),
    enabled: !!transfer_id,
  });

  useEffect(() => {
    if (transferManifestError) {
      toast.error('Error fetching transfer manifest');
    }
  }, [toast, transferManifest, transferManifestError]);

  /**
   * Constants for header information and profile card sections
   */
  const headerInfo = {
    header: 'Transfer Manifest Details',
    subHeader: `${transferManifest?.shipper_facility_name} -> ${transferManifest?.received_by_facility_name}`,
  };

  const profileCardSections: profileCardSectionT[] = [
    {
      title: 'Transfer Manifest Information',
      items: [
        {header: 'Manifest #', content: `${transferManifest?.manifest_number}`},
        {header: 'Package Count', content: `${packageCount || 'Not Available'}`},
        {header: 'Item Count', content: `${itemCount || 'Not Available'}`},
      ].filter(Boolean),
    },
    {
      title: 'Shipper Information',
      items: [
        {header: 'Name', content: `${transferManifest?.shipper_facility_name}`},
        {header: 'License #', content: `${transferManifest?.shipper_facility_license_number}`},
        {header: 'Date Shipped', content: `${transferManifest?.delivery_date_time || 'Not shipped yet'}`},
      ],
    },
    {
      title: 'Receiver Information',
      items: [
        {header: 'Name', content: `${transferManifest?.received_by_facility_name}`},
        {header: 'License #', content: `${transferManifest?.received_by_license_number}`},
        {header: 'Date Received', content: `${transferManifest?.received_date_time || 'Not received yet'}`},
      ],
    },
  ];

  /**
   * DataTable configuration for packages
   */
  const packageTableConfig = createDataTableConfig({
    table: 'Metric_Packages',
    title: 'Packages',
    columns: ['*'],
    filters: [{column: 'manifest_number', operator: 'eq', value: transferManifest?.manifest_number}],
    columnsDisplay: [
      {
        header: 'Metrc Pkg ID',
        field: 'metric_package_id',
      },
      {
        header: 'Package Type',
        field: 'package_type',
      },
      {
        header: 'Shipped Quantity',
        field: 'shipped_quantity',
      },
      {
        header: 'Received Quantity',
        field: 'received_quantity',
      },
      {
        header: 'Label',
        field: 'package_label',
      },
      {
        header: 'Name',
        field: 'product_name',
      },
      {
        header: 'Strain',
        field: 'product_strain_name',
      },
      {
        header: 'Quantity',
        field: 'quantity',
      },
    ],
    actionsDisplay: [
      {
        icon: 'ri-share-forward-line',
        color: 'primary',
        permission: () => true,
        handler: () => {
          if (orderItems.length > 0) {
            setIsModalOpen(true);
          } else {
            toast.error('No order items found');
          }
        },
      },
    ],
  });

  /**
   * DataTable configuration for deliveries
   */
  const deliveryTableConfig = createDataTableConfig({
    table: 'Metric_Delivery',
    title: 'Deliveries',
    columns: ['*'],
    filters: [{column: 'fk_metrc_transfer_id', operator: 'eq', value: transferManifest?.metrc_transfer_id}],
    columnsDisplay: [
      {
        header: 'Delivery Count',
        field: 'delivery_package_count',
      },
      {
        header: 'Arrival Date',
        field: 'actual_arrival_date_time',
      },
      {
        header: 'Shipment Type',
        field: 'shipment_type_name',
      },
      {
        header: 'Recipient',
        field: 'recipient_facility_name',
      },
      {
        header: 'Transaction Type',
        field: 'shipment_transaction_type',
      },
      {
        header: 'Received Count',
        field: 'delivery_received_package_count',
      },
    ],
  });

  /**
   * Tabs configuration for the ProfileDetailsTabListComponent
   */
  const profileTabs: profileDetailsTabListTabT[] = [
    {
      id: 'packages',
      title: 'Packages',
      icon: 'ri-boxing-line',
      content: <DataTable {...packageTableConfig} hideAdd hideSort />,
    },
    {
      id: 'deliveries',
      title: 'Deliveries',
      icon: 'ri-delivery-line',
      content: <DataTable {...deliveryTableConfig} hideAdd hideSort />,
    },
  ];

  return (
    <Page
      currentPage="Transfer-Manifest"
      handleBack={() => {
        navigate(`/admin/orders/${orderId}`);
      }}
    >
      <div className="grid grid-cols-12 gap-x-6">
        <ProfileCardComponent
          name={`${transferManifest?.manifest_number}`}
          headerInfo={headerInfo}
          sections={profileCardSections}
        />
        <ProfileDetailsTabListComponent tabs={profileTabs} className="col-span-12 xl:col-span-12 xxl:col-span-8" />
      </div>

      {/* Order Items Selection Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="max-h-[80vh] w-full max-w-2xl overflow-hidden rounded-lg bg-white shadow-xl">
            <div className="border-b border-gray-200 px-6 py-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Select Order Item</h3>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="text-gray-400 hover:text-gray-500 focus:outline-none"
                >
                  <span className="sr-only">Close</span>
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
            </div>
            <div className="max-h-[60vh] overflow-y-auto px-6 py-4">
              {orderItems.map((item, index) => (
                <div
                  key={index}
                  className="mb-4 flex cursor-pointer items-center justify-between rounded-lg border border-gray-200 p-4 hover:bg-gray-50"
                >
                  <div className="flex flex-col">
                    <span className="font-semibold text-gray-900">{item.product_name}</span>
                    <span className="text-sm text-gray-500">Quantity: {item.product_count}</span>
                  </div>
                  <i className="ri-arrow-right-line text-xl text-gray-400"></i>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </Page>
  );
}

export default TransferManifestDetails;
